import { Modal } from '@faststore/ui'
import {
  ChangeOrderModal,
  ProductCard as ProductCardUI,
} from '@plurix/ecom-components'
import { navigate } from 'gatsby'
import React, { memo, useMemo, useState } from 'react'
import { useRegionalizationContext } from 'src/local_modules/contexts/RegionalizationContext'
import { useProductCart } from 'src/local_modules/hooks/useProductCart'

import { HAS_WINDOW } from '../../../constants'
import { useClubContext } from '../../../contexts/ClubContext'
import { useSpecialPromotionsContext } from '../../../contexts/SpecialPromotionsContext'
import { useStoreContext } from '../../../contexts/StoreContext'
import { useAnalyticsEvents } from '../../../hooks/useAnalyticsEvents'
import { useBuyProduct } from '../../../hooks/useBuyProduct'
import { useSalesforceEvents } from '../../../hooks/useSalesforceEvents'
import { ClubModal } from '../../sections/ClubModal'
import { QuickView } from '../../sections/QuickView'
import { TelesalesModalProducts } from '../../sections/Telesales/TelesalesModalProducts'

import type { PromotionalProductPrices } from '../../../hooks/useUTMPromotion'
import type {
  ProductLayout,
  ProductsQueryResponse,
} from '../../../types/Product'

import '@plurix/ecom-components/dist/styles/product-card.global.css'
import './product-card.scss'
// import './product-card-horizontal.scss'

export type Variant = 'horizontal' | 'vertical'

interface ProductCardProps {
  product: ProductsQueryResponse['node']
  titleLevel?: number
  location: string
  bordered?: boolean
  outOfStock?: boolean
  variant?: Variant
  hasPromotionalPrice?: PromotionalProductPrices
  defaultScListPrice?: number
  clubPrice?: number
  hasQuickView?: boolean
  layout?: ProductLayout
}
function ProductCard({
  product,
  location,
  hasPromotionalPrice,
  defaultScListPrice,
  clubPrice,
  hasQuickView = true,
  layout = 'vertical',
}: ProductCardProps) {
  const {
    priceWithoutPromotions,
    customOffers: {
      listPriceCustom: nativeListPrice,
      spotPriceCustom: nativeSpotPrice,
    },
  } = product

  const { listPrice: promotionalListPrice, spotPrice: promotionalSpotPrice } =
    hasPromotionalPrice ?? {}

  const { spotPriceCustom } = useMemo(() => {
    if (
      promotionalListPrice &&
      promotionalSpotPrice &&
      promotionalSpotPrice !== nativeSpotPrice
    ) {
      return {
        listPriceCustom: promotionalListPrice,
        spotPriceCustom: promotionalSpotPrice,
      }
    }

    return {
      listPriceCustom: nativeListPrice,
      spotPriceCustom: nativeSpotPrice,
    }
  }, [
    promotionalListPrice,
    promotionalSpotPrice,
    nativeSpotPrice,
    nativeListPrice,
  ])

  const [openClubModal, setOpenClubModal] = useState(false)
  const [openQuickView, setOpenQuickView] = useState(false)
  const [openTelesalesModal, setOpenTelesalesModal] = useState(false)

  const { buyProps, cartProductId, cartProductQty } = useBuyProduct(product)

  const { hasClub, activatePlusFriendBadge, hasChangeOrder } = useStoreContext()
  const { postalCode } = useRegionalizationContext()
  const { isClubClient } = useClubContext()

  const { selectItemEvent } = useAnalyticsEvents()
  const { selectItemEventSalesforce } = useSalesforceEvents()
  const { searchPromotion } = useSpecialPromotionsContext()
  const {
    quantitySelector,
    handleAddToCart,
    modalChangeOrder,
    setModalChangeOrder,
    orderFormId,
    productInCart,
  } = useProductCart({
    product,
    location,
    clubPrice,
    setOpenClubModal,
  })

  const promotion = searchPromotion?.(product)

  const clubModalCanBeOpened = HAS_WINDOW && hasClub && !isClubClient

  const triggerSelectItemEvent = () => {
    selectItemEvent(product)
    selectItemEventSalesforce(product)

    if (hasQuickView) {
      quickViewEvent(product, location)
    }
  }

  const IsTelesales = location === 'plp-telesales-search-page-0'

  const { quickViewEvent } = useAnalyticsEvents()

  const handleClick = () => {
    if (IsTelesales) {
      setOpenTelesalesModal(true)

      return
    }

    triggerSelectItemEvent()

    if (hasQuickView) {
      setOpenQuickView(true)
    } else {
      navigate(`/${product.slug}/p`)
    }
  }

  return (
    <>
      {!IsTelesales && clubModalCanBeOpened && (
        <Modal isOpen={openClubModal} className="modal-regionalization">
          <ClubModal
            setOpenClubModal={setOpenClubModal}
            clubPrice={clubPrice}
            buyProps={buyProps}
          />
        </Modal>
      )}

      {IsTelesales && (
        <TelesalesModalProducts
          openModal={openTelesalesModal}
          setOpenModal={setOpenTelesalesModal}
          price={priceWithoutPromotions}
          buyProps={buyProps}
          cartProductId={cartProductId}
          hasClub={hasClub}
          cartProductQty={cartProductQty}
          product={product}
          isClubClient={isClubClient ?? false}
          defaultScListPrice={defaultScListPrice}
          clubPrice={clubPrice}
          spotPriceCustom={spotPriceCustom}
        />
      )}

      {!openClubModal && !IsTelesales && openQuickView && (
        <QuickView
          openQuickView={openQuickView}
          setOpenQuickView={setOpenQuickView}
          product={product}
          openClubModal={openClubModal}
          setOpenClubModal={setOpenClubModal}
          defaultScListPrice={defaultScListPrice}
          clubPrice={clubPrice}
        />
      )}
      {modalChangeOrder && postalCode && Boolean(hasChangeOrder) && (
        <ChangeOrderModal
          postalCode={postalCode}
          isOpen={modalChangeOrder}
          setIsOpen={setModalChangeOrder}
          environment="faststore"
          selectedItem={{
            id: product.id,
            imageUrl: product.image[0].url,
            name: product.name,
            quantity: product.unitMultiplier,
            sellingPrice: product.offers.offers[0].price,
            measurementUnit: product.measurementUnit ?? '',
            unitMultiplier: product.unitMultiplier,
            productOriginId: product.id,
          }}
          orderFormId={orderFormId}
        />
      )}

      <ProductCardUI
        product={product}
        variant={layout}
        hasPlusFriendBadge={activatePlusFriendBadge}
        hasPromotionalPrice={hasPromotionalPrice}
        clubPrice={clubPrice}
        defaultScListPrice={defaultScListPrice}
        hasClub={hasClub}
        isClubClient={isClubClient}
        openModal={handleClick}
        promotion={promotion ? { ...promotion, textColor: 'white' } : undefined}
        addToCartAction={handleAddToCart}
        quantitySelectorAction={quantitySelector}
        productInCart={productInCart}
      />
    </>
  )
}

export default memo(ProductCard)
